import { useEffect, useState } from "react";
import { api, apiError, inr } from "@/lib/api";
import { toast } from "sonner";
import { Download, AlertTriangle, Clock, Ship } from "lucide-react";

const now = new Date();
const START = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
const FYS = Array.from({ length: 6 }, (_, i) => `${START - i}-${String((START - i + 1) % 100).padStart(2, "0")}`);
const TH = "px-2 py-2 text-left";
const THR = "px-2 py-2 text-right";

function Stat({ icon: Icon, label, value, sub, testid, tone }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 small-label"><Icon className={`h-4 w-4 ${tone}`} /> {label}</div>
      <div className="font-mono text-lg mt-1">{value}</div>
      <div className="text-xs text-muted-foreground font-mono">{sub}</div>
    </div>
  );
}

function PendingTable({ title, rows, testid, boe }) {
  return (
    <div className="rounded-lg border border-border bg-card overflow-x-auto" data-testid={testid}>
      <div className="px-4 py-2.5 border-b border-border font-head font-semibold text-sm">{title}</div>
      <table className="w-full text-[11px] font-mono">
        <thead className="text-muted-foreground border-b border-border"><tr>
          <th className={TH}>2B period</th><th className={TH}>Section</th>{!boe && <th className={TH}>Supplier</th>}
          <th className={TH}>{boe ? "BOE No" : "Invoice No"}</th><th className={TH}>Date</th>
          <th className={THR}>Taxable</th><th className={THR}>Tax</th><th className={THR}>Pending (days)</th>
        </tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.twob_period}-${r.invoice_no}-${i}`} className="border-b border-border/40 [&>td]:px-2 [&>td]:py-1.5">
              <td>{r.twob_period}</td><td>{r.section}</td>
              {!boe && <td className="font-sans">{r.counterparty_name || r.counterparty_gstin}<div className="font-mono text-[10px] text-muted-foreground">{r.counterparty_gstin}</div></td>}
              <td className="whitespace-nowrap">{r.invoice_no}</td><td className="whitespace-nowrap">{r.invoice_date}</td>
              <td className="text-right">{inr(r.taxable_value)}</td><td className="text-right">{inr(r.tax)}</td>
              <td className={`text-right ${r.age_days > 90 ? "text-rose-400" : r.age_days > 30 ? "text-amber-400" : ""}`}>{r.age_days}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={8} className="px-2 py-4 text-center text-muted-foreground font-sans">Nothing pending</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function BlockedTable({ rows }) {
  return (
    <div className="rounded-lg border border-border bg-card overflow-x-auto" data-testid="blocked-itc-table">
      <div className="px-4 py-2.5 border-b border-border font-head font-semibold text-sm">Possible blocked credit — Section 17(5)</div>
      <table className="w-full text-[11px] font-mono">
        <thead className="text-muted-foreground border-b border-border"><tr>
          <th className={TH}>Period</th><th className={TH}>Invoice</th><th className={TH}>Supplier</th><th className={TH}>Description / HSN</th>
          <th className={THR}>Tax</th><th className={TH}>Section</th><th className={TH}>Why flagged</th><th className={TH}>Status</th>
        </tr></thead>
        <tbody>
          {rows.map((b, i) => (
            <tr key={`${b.period}-${b.invoice_no}-${i}`} className="border-b border-border/40 [&>td]:px-2 [&>td]:py-1.5" data-testid={`blocked-row-${b.invoice_no}`}>
              <td>{b.period}</td><td className="whitespace-nowrap">{b.invoice_no}</td><td className="font-sans">{b.counterparty_name || b.counterparty_gstin}</td>
              <td className="font-sans min-w-[180px]">{b.description || "—"}<div className="font-mono text-[10px] text-muted-foreground">HSN {b.hsn || "—"}</div></td>
              <td className="text-right">{inr(b.tax)}</td>
              <td className="whitespace-nowrap" title={b.label}>{b.clause}</td><td className="font-sans min-w-[260px]">{b.label} — {b.reason}</td>
              <td className={`whitespace-nowrap ${b.reversed ? "text-emerald-400" : "text-rose-400"}`}>{b.status}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={8} className="px-2 py-4 text-center text-muted-foreground font-sans">No purchases flagged</td></tr>}
        </tbody>
      </table>
      <p className="px-4 py-2 text-xs text-muted-foreground">Flags are suggestions from the description, HSN/SAC and Eligibility columns. To reverse, set Reversal Type "Other Reversals [17(5)]" in the Purchase Master and re-import.</p>
    </div>
  );
}

export function ItcChecksPanel({ clientId }) {
  const [fy, setFy] = useState(FYS[0]);
  const [rep, setRep] = useState(null);
  useEffect(() => {
    api.get(`/clients/${clientId}/reports/itc-checks?fy=${fy}`).then((r) => setRep(r.data)).catch((e) => toast.error(apiError(e)));
  }, [clientId, fy]);
  const download = async () => {
    try {
      const res = await api.get(`/clients/${clientId}/reports/itc-checks/export?fy=${fy}`, { responseType: "blob" });
      const m = (res.headers["content-disposition"] || "").match(/filename\*=UTF-8''([^;]+)/);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data); a.download = m ? decodeURIComponent(m[1]) : `${fy}_2B_Pending_Blocked_ITC.xlsx`; a.click(); URL.revokeObjectURL(a.href);
      toast.success("Downloaded");
    } catch (e) { toast.error(apiError(e)); }
  };
  const s = rep?.summary;
  return (
    <div className="space-y-4" data-testid="itc-checks-panel">
      <div className="flex flex-wrap items-center gap-2">
        <select data-testid="itc-checks-fy-select" value={fy} onChange={(e) => setFy(e.target.value)} className="rounded-md bg-card border border-border px-3 py-1.5 text-sm font-mono">
          {FYS.map((f) => <option key={f} value={f}>FY {f}</option>)}
        </select>
        <button data-testid="itc-checks-download-button" onClick={download} className="flex items-center gap-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 text-xs font-medium"><Download className="h-3.5 w-3.5" /> Download Excel</button>
        {s && <span className="text-xs text-muted-foreground">GSTR-2B loaded for {s.twob_months_loaded} month(s). Pending = in GSTR-2B but not in your purchase register.</span>}
      </div>
      {s && <>
        <div className="grid gap-3 sm:grid-cols-3">
          <Stat icon={Clock} tone="text-amber-400" label="2B invoices pending" value={s.pending_b2b_count} sub={`ITC ${inr(s.pending_b2b_tax)}`} testid="stat-pending-b2b" />
          <Stat icon={Ship} tone="text-cyan-400" label="Import BOE pending" value={s.pending_boe_count} sub={`IGST ${inr(s.pending_boe_tax)}`} testid="stat-pending-boe" />
          <Stat icon={AlertTriangle} tone="text-rose-400" label="Blocked ITC flags" value={s.blocked_count} sub={`Not reversed ${inr(s.blocked_tax_unreversed)}`} testid="stat-blocked" />
        </div>
        <PendingTable title="GSTR-2B invoices pending in books" rows={rep.pending_b2b} testid="pending-b2b-table" />
        <PendingTable title="Import bills of entry (BOE) in GSTR-2B pending in books" rows={rep.pending_boe} testid="pending-boe-table" boe />
        {rep.pending_cdn?.length > 0 && <PendingTable title={`Credit / debit notes in GSTR-2B pending in books (net tax ${inr(s.pending_cdn_tax)})`} rows={rep.pending_cdn} testid="pending-cdn-table" />}
        <BlockedTable rows={rep.blocked} />
      </>}
    </div>
  );
}
